import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase, getSupabaseClient, db } from '@/lib/supabase'
import {
  PRODUCT_ROLE_PERMISSIONS,
  type Permission,
  type ProductRole,
  type ProfileInput,
  type SyncSettingsInput,
  type UserRole,
} from '@dcp/shared'

export interface Profile {
  id: string
  email: string
  fullName: string | null
  organizationId: string | null
  activeOrganizationId: string | null
  role: UserRole
  /**
   * Feature-level role driving the product-role permissions matrix, resolved
   * FOR THE ORGANIZATION THE USER IS CURRENTLY IN (DCP-350).
   *
   * A person can belong to several organizations with a different role in each
   * — Admin in one, View Only in another — so this is read from their
   * membership row, falling back to `profiles.product_role` when there is
   * none. Null for platform staff (super_admin/admin/tech_team) and legacy
   * users that never went through an explicit assignment.
   *
   * Consumers are unchanged by DCP-350: they keep calling
   * `hasPermission(profile.productRole, …)`, they just get the right role now.
   */
  productRole: ProductRole | null
  /**
   * Convenience projection of `productRole` through PRODUCT_ROLE_PERMISSIONS.
   * Empty array when productRole is null. Use this when a component needs to
   * branch on multiple permissions; for single-permission gates prefer
   * `hasPermission(productRole, ...)` from `@dcp/shared`.
   */
  permissions: readonly Permission[]
  createdAt: string
  updatedAt: string
}

/**
 * Resolve the permission list for a given product role.
 * Returns an empty array for null/undefined roles so callers can iterate
 * without a separate null check.
 */
function resolvePermissions(productRole: ProductRole | null): readonly Permission[] {
  if (productRole === null) return []
  return PRODUCT_ROLE_PERMISSIONS[productRole]
}

export interface SyncSettings {
  autoSync: boolean
  syncInterval: 'hourly' | 'daily' | 'weekly'
  notifyOnError: boolean
  notifyOnSuccess: boolean
}

const PROFILE_KEY = ['profile']
const SYNC_SETTINGS_KEY = ['sync-settings']

/**
 * Maximum retries when the initial profile fetch returns
 * `organization_id = null` for a freshly-signed-in user.
 */
const MAX_PROFILE_RETRIES_ON_NULL_ORG = 3

/** Delay between profile retries while waiting for the handle_new_user trigger. */
const PROFILE_RETRY_DELAY_MS = 500

/**
 * Sessions older than this window have a legitimately-null org_id (no
 * race) — skip the retry path.
 */
const FRESH_SESSION_WINDOW_MS = 30_000

/**
 * Raw shape returned by the `profiles` table select.
 * Kept local to this module to avoid pulling in generated Supabase types.
 */
type ProfileRow = {
  id: string
  email: string
  full_name: string | null
  organization_id: string | null
  active_organization_id: string | null
  role: string
  product_role: ProductRole | null
  created_at: string
  updated_at: string
}

/**
 * Maps a raw database profile row to the application-level `Profile` shape.
 * Also resolves the permission list from `product_role` so callers don't
 * have to recompute it on every render.
 *
 * @param row - Raw row from the `profiles` table.
 * @returns Camel-cased `Profile` with a typed `UserRole` and `permissions`.
 */
function mapProfileRow(row: ProfileRow, membershipRole?: ProductRole | null): Profile {
  // Coerce undefined to null so consumers don't have to handle three states
  // (undefined / null / value). The DB column is nullable; legacy rows
  // without product_role return null after backfill. The `?? null` is
  // load-bearing: a row that omits the key entirely reads as undefined at
  // runtime even though ProfileRow types it as ProductRole | null.
  // DCP-350: the membership row wins when it carries a role. `undefined` means
  // "no membership row was read" (platform staff, or the lookup failed) and
  // falls through to the profile column, which is what every pre-DCP-350 user
  // has — so single-org behaviour is bit-for-bit unchanged.
  const productRole =
    membershipRole !== undefined && membershipRole !== null
      ? membershipRole
      : ((row.product_role as ProductRole | null) ?? null)
  return {
    id: row.id,
    email: row.email,
    fullName: row.full_name,
    organizationId: row.organization_id,
    activeOrganizationId: row.active_organization_id,
    role: row.role as UserRole,
    productRole,
    permissions: resolvePermissions(productRole),
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }
}

/**
 * Read the caller's role in one organization from `user_organization_access`
 * (DCP-350).
 *
 * Returns `undefined` when there is nothing to apply — no organization in
 * context, no membership row, or a failed read — so `mapProfileRow` falls back
 * to `profiles.product_role` and behaviour matches the pre-DCP-350 world.
 * Deliberately non-throwing: a permissions read that fails should degrade to
 * the profile role, not blank the app.
 *
 * @param client - Supabase client bound to the current session
 * @param userId - Whose membership to read
 * @param organizationId - Organization the user is currently working in
 */
async function fetchMembershipRole(
  client: ReturnType<typeof getSupabaseClient>,
  userId: string,
  organizationId: string | null,
): Promise<ProductRole | undefined> {
  if (!organizationId) return undefined

  const { data, error } = await client
    .from('user_organization_access')
    .select('product_role')
    .eq('user_id', userId)
    .eq('organization_id', organizationId)
    .maybeSingle()

  if (error) {
    console.error('Error fetching membership role:', error)
    return undefined
  }

  return (data?.product_role as ProductRole | null) ?? undefined
}

/**
 * Returns true when the session is fresh enough that a null `organization_id`
 * may indicate the handle_new_user trigger hasn't committed yet (e.g. the
 * invitation-accept → dashboard mount race). Older sessions are not subject
 * to the retry path even if their org is legitimately null.
 */
function isFreshSession(lastSignInAt: string | null | undefined): boolean {
  if (!lastSignInAt) return false
  const ageMs = Date.now() - new Date(lastSignInAt).getTime()
  return ageMs < FRESH_SESSION_WINDOW_MS
}

/**
 * Re-fetches the profile row with a short backoff while `organization_id`
 * is still null. Returns as soon as the org resolves, the retry budget is
 * exhausted, or a query error occurs — whichever comes first.
 *
 * Query errors break the loop (logged, returning the last-read row) rather
 * than throwing: stale render beats hanging on a partial outage.
 *
 * @param initial - Row from the initial select; returned unchanged if all
 *   retries also yield null.
 */
async function refetchProfileUntilOrgResolved(
  client: ReturnType<typeof getSupabaseClient>,
  userId: string,
  initial: ProfileRow,
): Promise<ProfileRow> {
  let current = initial
  for (let attempt = 0; attempt < MAX_PROFILE_RETRIES_ON_NULL_ORG; attempt++) {
    await new Promise<void>(r => setTimeout(r, PROFILE_RETRY_DELAY_MS))

    const { data, error } = await client
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single()

    if (error) {
      console.error('Error re-fetching profile during retry:', error)
      return current
    }

    current = data
    if (current.organization_id !== null) return current
  }
  return current
}

/**
 * Hook to fetch the current user's profile
 * Returns profile data including role, organization_id, and active_organization_id
 */
export function useProfile() {
  return useQuery({
    queryKey: PROFILE_KEY,
    queryFn: async (): Promise<Profile | null> => {
      if (!supabase) return null

      const client = getSupabaseClient()

      const { data: { user } } = await client.auth.getUser()
      if (!user) return null

      // Query profile directly to include active_organization_id
      const { data, error } = await client
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single()

      if (error) {
        console.error('Error fetching profile:', error)
        return null
      }

      // Defense-in-depth: if organization_id is null but the session is
      // fresh, the handle_new_user trigger may not have committed yet
      // (invitation-accept → dashboard race). Poll briefly until the org
      // resolves; see refetchProfileUntilOrgResolved for the retry budget
      // and error handling.
      const profileData =
        data.organization_id === null && isFreshSession(user.last_sign_in_at)
          ? await refetchProfileUntilOrgResolved(client, user.id, data)
          : data

      // DCP-350: which organization is this session working in? The active one
      // when the user has picked it, otherwise their home organization. Reading
      // it from the profile row (rather than from OrganizationContext) keeps
      // this hook free of a circular dependency — the context is built ON this
      // query — and means switching organizations, which persists
      // active_organization_id and invalidates this key, re-reads the role.
      const membershipRole = await fetchMembershipRole(
        client,
        user.id,
        profileData.active_organization_id ?? profileData.organization_id,
      )

      return mapProfileRow(profileData, membershipRole)
    },
  })
}

export function useUpdateProfile() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: ProfileInput): Promise<Profile> => {
      const client = getSupabaseClient()

      const { data: { user } } = await client.auth.getUser()
      if (!user) throw new Error('Not authenticated')

      const { data, error} = await db.profiles.update(user.id, {
        full_name: input.fullName,
        updated_at: new Date().toISOString(),
      })

      if (error) throw error

      // Read the membership role for the organization in use, exactly as
      // useProfile does. Without it the returned Profile would carry the HOME
      // organization's role, so anything reading the mutation result directly
      // (rather than waiting for the invalidation below) would briefly see the
      // wrong permissions for a multi-org user (Presto PR #202 finding 5).
      const membershipRole = await fetchMembershipRole(
        client,
        user.id,
        data.active_organization_id ?? data.organization_id,
      )

      return mapProfileRow(data, membershipRole)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PROFILE_KEY })
    },
  })
}

export function useSyncSettings() {
  return useQuery({
    queryKey: SYNC_SETTINGS_KEY,
    queryFn: async (): Promise<SyncSettings> => {
      // For now, return from localStorage or defaults
      // Later this can be stored in Supabase
      const stored = localStorage.getItem('syncSettings')
      if (stored) {
        return JSON.parse(stored)
      }
      return {
        autoSync: false,
        syncInterval: 'daily',
        notifyOnError: true,
        notifyOnSuccess: false,
      }
    },
  })
}

export function useUpdateSyncSettings() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: SyncSettingsInput) => {
      // Store in localStorage for now
      localStorage.setItem('syncSettings', JSON.stringify(input))
      return input
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SYNC_SETTINGS_KEY })
    },
  })
}

/**
 * Hook to change the user's password
 * Note: Supabase Auth doesn't require current password verification for authenticated users
 */
export function useChangePassword() {
  return useMutation({
    mutationFn: async ({ newPassword }: { newPassword: string }) => {
      const client = getSupabaseClient()

      const { error } = await client.auth.updateUser({
        password: newPassword,
      })

      if (error) throw error
      return { success: true }
    },
  })
}

export function useDeleteAccount() {
  return useMutation({
    mutationFn: async () => {
      const client = getSupabaseClient()

      // Note: This requires service_role key on backend
      // For now, just sign out
      const { error } = await client.auth.signOut()
      if (error) throw error

      return { success: true }
    },
  })
}

/**
 * Hook to update the user's active organization
 * Used by platform staff (super_admin, tech_team) to switch organizations
 */
export function useUpdateActiveOrganization() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (organizationId: string): Promise<void> => {
      const client = getSupabaseClient()

      const { data: { user } } = await client.auth.getUser()
      if (!user) throw new Error('Not authenticated')

      const { error } = await client
        .from('profiles')
        .update({ active_organization_id: organizationId })
        .eq('id', user.id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PROFILE_KEY })
    },
  })
}